import React, { useState, useEffect } from 'react';
import * as api from '../utils/api';
import styles from './WithdrawModal.module.css';
import { FaTimes, FaArrowLeft, FaBackspace, FaCheckCircle, FaUniversity, FaWallet } from 'react-icons/fa';
import MessageBox from './MessageBox';
import { useAuth } from '../context/AuthContext';

function WithdrawModal({ onClose }) {
  const [step, setStep] = useState(1);
  const [amount, setAmount] = useState('');
  const [user, setUser] = useState(null);
  const [message, setMessage] = useState(null);
  const { user: authUser } = useAuth();
  const [isInfoLineHighlighted, setInfoLineHighlighted] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const { data } = await api.getUser();
        setUser(data);
      } catch (err) {
        console.error("Error fetching user:", err);
      }
    };
    fetchUserData();
  }, []);

  const handleProceed = () => {
    if (!amount || parseFloat(amount) < 500 || parseFloat(amount) > 500000) {
      setInfoLineHighlighted(true);
      setTimeout(() => setInfoLineHighlighted(false), 2000);
      return;
    }

    if (user) {
      const withdrawable = Number(user.withdrawalFund || 0);
      if (Number(amount) > withdrawable) {
        setInfoLineHighlighted(true);
        setTimeout(() => setInfoLineHighlighted(false), 2000);
        return;
      }
    }

    setStep(2);
  };

  const handleInitiateTransaction = async () => {
    try {
      const txnData = {
        txnType: 'withdraw',
        txnAmount: amount,
        balanceType: 'Indian',
        name: authUser?.name || user?.name || '',
      };
      await api.initiateTransaction(txnData);
      setStep(3); // Go straight to success/submitted state
    } catch (err) {
      const msg = err.response?.data?.msg || 'An unexpected error occurred. Please try again.';
      setMessage({ type: 'error', text: `Transaction failed: ${msg}` });
    }
  };

  const handleCloseMessage = () => setMessage(null);

  const getTitle = () => {
    if (step === 1) return 'Withdraw Funds';
    if (step === 2) return 'Confirm Bank';
    if (step === 3) return 'Success';
    if (step === 4) return 'Failed';
    return 'Withdrawal';
  };

  const handleKeypadInput = (val) => {
    if (val === 'backspace') {
      setAmount((prev) => prev.slice(0, -1));
    } else if (val === 'clear') {
      setAmount('');
    } else {
      setAmount((prev) => {
        if (prev.length >= 8) return prev;
        return prev === '0' ? val : prev + val;
      });
    }
  };

  const renderStep1 = () => (
    <div className={styles['step-container']}>
      <div className={styles['available-card']}>
        <FaWallet className={styles['available-icon']} />
        <div className={styles['available-text']}>
          <label>Withdrawable Balance</label>
          <p>₹{Number(user?.withdrawalFund || 0).toLocaleString()}</p>
        </div>
      </div>

      <div className={styles['amount-input-card']}>
        <div className={styles['amount-display-large']}>
          <span className={styles['currency']}>₹</span>
          <span className={styles['value']}>{amount || '0'}</span>
        </div>
        <p className={`${styles['amount-limit-info']} ${isInfoLineHighlighted ? styles.highlight : ''}`}>
          {user && parseFloat(amount) > user.withdrawalFund 
            ? 'Insufficient withdrawable balance' 
            : 'Min: ₹500 • Max: ₹5,00,000'}
        </p>
      </div>

      <div className={styles['keypad-grid']}>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, '.', 0, 'backspace'].map((key) => (
          <button
            key={key}
            onClick={() => handleKeypadInput(key)}
            className={`${styles['keypad-digit']} ${key === 'backspace' ? styles.keypad_backspace : ''}`}
          >
            {key === 'backspace' ? <FaBackspace /> : key}
          </button>
        ))}
      </div>

      <div className={styles['action-footer']}>
        <button
          className={styles['primary-full-btn']}
          onClick={handleProceed}
          disabled={!user}
        >
          Withdraw Funds
        </button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className={styles['step-container']}>
      <div className={styles['bank-info-banner']}>
        <p>Funds will be credited to your linked bank account</p>
      </div>

      <div className={styles['details-list']}>
        <div className={styles['detail-card']}>
          <div className={styles['detail-content']}>
            <label>Linked Bank</label>
            <p>{user?.bankName}</p>
          </div>
          <FaUniversity className={styles['detail-icon']} />
        </div>
        <div className={styles['detail-card']}>
          <div className={styles['detail-content']}>
            <label>Account Number</label>
            <p>{user?.accountNumber}</p>
          </div>
        </div>
        <div className={styles['detail-card']}>
          <div className={styles['detail-content']}>
            <label>Withdrawal Amount</label>
            <p style={{ color: 'var(--primary-color)' }}>₹{parseFloat(amount).toLocaleString()}</p>
          </div>
        </div>
      </div>

      <div className={styles['action-footer']}>
        <button className={styles['primary-full-btn']} onClick={handleInitiateTransaction}>
          Confirm Withdrawal
        </button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className={styles['step-container']}>
      <div className={styles['success-animation']}>
        <FaCheckCircle />
      </div>
      <h3 className={styles['success-title']}>Withdrawal Requested</h3>
      <p className={styles['success-text']}>Your request has been submitted. The amount will be credited to your bank account within 24-48 working hours.</p>
      
      <div className={styles['action-footer']}>
        <button className={styles['primary-full-btn']} onClick={onClose}>
          Done
        </button>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className={styles['step-container']}>
      <div className={styles['success-animation']} style={{ color: '#ff4d4d' }}>
        <FaTimes />
      </div>
      <h3 className={styles['success-title']} style={{ color: '#ff4d4d' }}>Withdrawal Rejected</h3>
      <p className={styles['success-text']}>Your withdrawal request could not be processed. Please check your details or contact support.</p>

      <div className={styles['action-footer']}>
        <button className={styles['primary-full-btn']} style={{ background: '#ff4d4d' }} onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div className={styles['modal-overlay']}>
        {message && (
          <MessageBox
            message={message.text}
            type={message.type}
            onClose={handleCloseMessage}
          />
        )}
        <div className={styles['modal-card']}>
          <div className={styles['modal-header-premium']}>
            <div className={styles['header-left']}>
              {step === 2 && (
                <button className={styles['back-btn']} onClick={() => setStep(1)}>
                  <FaArrowLeft />
                </button>
              )}
              <h3>{getTitle()}</h3>
            </div>
            <button className={styles['close-btn']} onClick={onClose}>
              <FaTimes />
            </button>
          </div>
          
          <div className={styles['modal-body']}>
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
            {step === 4 && renderStep4()}
          </div>
        </div>
      </div>
    </>
  );
}

export default WithdrawModal;
